import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Mic, MicOff, Volume2, VolumeX, Globe, X, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'english', name: 'English', flag: 'EN' },
  { code: 'hindi', name: 'Hindi', flag: 'HI' },
  { code: 'tamil', name: 'Tamil', flag: 'TA' },
  { code: 'telugu', name: 'Telugu', flag: 'TE' },
  { code: 'malayalam', name: 'Malayalam', flag: 'ML' },
];

const WAKE_PHRASES = ['hey archana', 'hi archana', 'okay archana', 'hello archana', 'archana'];

const VOICES = [
  { id: 'nova', name: 'Nova (Energetic)' },
  { id: 'alloy', name: 'Alloy (Neutral)' },
  { id: 'shimmer', name: 'Shimmer (Cheerful)' },
  { id: 'echo', name: 'Echo (Calm)' },
];

export default function VoiceAssistant({ designContext, onClose }) {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [wakeWordDetected, setWakeWordDetected] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [language, setLanguage] = useState('english');
  const [voice, setVoice] = useState('nova');
  const [showSettings, setShowSettings] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isAlwaysListening, setIsAlwaysListening] = useState(true);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const audioRef = useRef(new Audio());
  const streamRef = useRef(null);

  // Initialize Web Speech API for wake word detection
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const last = event.results.length - 1;
        const text = event.results[last][0].transcript.toLowerCase();
        
        // Check for wake word
        if (!wakeWordDetected && !isProcessing) {
          const hasWakeWord = WAKE_PHRASES.some(phrase => text.includes(phrase));
          if (hasWakeWord) {
            setWakeWordDetected(true);
            toast.success('Yes? How can I help you?', { duration: 2000 });
            // Start recording after wake word
            setTimeout(() => startRecording(), 500);
          }
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        if (event.error !== 'no-speech') {
          console.log('Speech recognition error:', event.error);
        }
      };
      
      recognitionRef.current.onend = () => {
        // Restart if always listening and not processing
        if (isAlwaysListening && !isProcessing && !wakeWordDetected) {
          try {
            recognitionRef.current?.start();
          } catch (e) {
            // Already started
          }
        }
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isAlwaysListening, isProcessing, wakeWordDetected]);

  // Start/stop always listening
  useEffect(() => {
    if (isAlwaysListening && recognitionRef.current && !isProcessing) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (e) {
        // Already started
      }
    } else if (!isAlwaysListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, [isAlwaysListening, isProcessing]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsListening(true);
      
      // Auto-stop after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, 10000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone');
      setWakeWordDetected(false);
    }
  }, [language]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
    }
    setIsListening(false);
  }, []);

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    setWakeWordDetected(false);
    
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        // Step 1: Transcribe
        const transcribeRes = await axios.post(`${API}/voice/transcribe`, {
          audio_base64: base64Audio,
          language: language,
          design_context: designContext
        }, { withCredentials: true });
        
        const userText = transcribeRes.data.text;
        setTranscript(userText);
        
        if (!userText || userText.trim().length < 2) {
          toast.error('Could not understand. Please try again.');
          setIsProcessing(false);
          return;
        }
        
        // Add to history
        setConversationHistory(prev => [...prev, { role: 'user', text: userText }]);
        
        // Step 2: Get AI response with audio
        const chatRes = await axios.post(`${API}/voice/chat`, {
          message: userText,
          language: language,
          design_context: designContext,
          voice: voice
        }, { withCredentials: true });
        
        setResponse(chatRes.data.text);
        setConversationHistory(prev => [...prev, { role: 'assistant', text: chatRes.data.text }]);
        
        // Step 3: Play audio
        if (chatRes.data.audio_base64) {
          playAudio(chatRes.data.audio_base64);
        }
        
        setIsProcessing(false);
      };
    } catch (error) {
      console.error('Voice processing error:', error);
      toast.error('Failed to process voice. Please try again.');
      setIsProcessing(false);
    }
  };

  const playAudio = (base64Audio) => {
    setIsSpeaking(true);
    const audio = audioRef.current;
    audio.src = `data:audio/mp3;base64,${base64Audio}`;
    audio.onended = () => setIsSpeaking(false);
    audio.onerror = () => {
      setIsSpeaking(false);
      toast.error('Audio playback failed');
    };
    audio.play().catch(err => {
      console.error('Audio play error:', err);
      setIsSpeaking(false);
    });
  };

  const stopAudio = () => {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsSpeaking(false);
  };

  const toggleListening = () => {
    if (wakeWordDetected || isListening) {
      stopRecording();
      setWakeWordDetected(false);
    } else {
      setWakeWordDetected(true);
      startRecording();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="voice-assistant">
      {/* Main Panel */}
      <div className="bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl w-80 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-gradient-to-r from-blue-600/20 to-purple-600/20">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isAlwaysListening ? 'bg-green-400 animate-pulse' : 'bg-slate-500'}`} />
            <span className="font-chivo font-semibold text-white text-sm">Archana Voice Assistant</span>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowSettings(!showSettings)}
              className="h-7 w-7 p-0 text-slate-400 hover:text-white"
              data-testid="voice-settings-btn"
            >
              <Globe className="w-3.5 h-3.5" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="h-7 w-7 p-0 text-slate-400 hover:text-white"
              data-testid="voice-close-btn"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="px-4 py-3 border-b border-white/5 bg-slate-800/50 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Language</label>
              <div className="flex flex-wrap gap-1">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                      language === lang.code 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Voice</label>
              <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                className="w-full bg-slate-700 border-none rounded px-2 py-1.5 text-xs text-white"
                data-testid="voice-select"
              >
                {VOICES.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Always Listening</span>
              <button
                onClick={() => setIsAlwaysListening(!isAlwaysListening)}
                className={`w-10 h-5 rounded-full transition-colors ${isAlwaysListening ? 'bg-blue-600' : 'bg-slate-600'}`}
                data-testid="always-listen-toggle"
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isAlwaysListening ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        )}

        {/* Conversation */}
        <div className="h-48 overflow-y-auto px-4 py-3 space-y-2" data-testid="voice-conversation">
          {conversationHistory.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Sparkles className="w-8 h-8 text-blue-400 mb-2 opacity-50" />
              <p className="text-xs text-slate-500 leading-relaxed">
                Say <span className="text-blue-400 font-semibold">"Hey Archana"</span><br />
                to start a conversation
              </p>
            </div>
          ) : (
            conversationHistory.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-slate-700 text-slate-200 rounded-bl-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Status */}
        <div className="px-4 py-2 border-t border-white/5 bg-slate-800/30">
          <div className="flex items-center justify-center gap-2 text-[10px]">
            {isProcessing ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
                <span className="text-blue-400">Processing...</span>
              </>
            ) : isSpeaking ? (
              <>
                <Volume2 className="w-3 h-3 text-green-400 animate-pulse" />
                <span className="text-green-400">Speaking...</span>
              </>
            ) : wakeWordDetected ? (
              <>
                <Mic className="w-3 h-3 text-red-400 animate-pulse" />
                <span className="text-red-400">Listening... Speak now!</span>
              </>
            ) : isAlwaysListening ? (
              <>
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-slate-500">Waiting for "Hey Archana"...</span>
              </>
            ) : (
              <span className="text-slate-500">Press mic to speak</span>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="px-4 py-3 border-t border-white/5 flex items-center justify-center gap-3">
          <Button
            onClick={toggleListening}
            disabled={isProcessing}
            className={`w-14 h-14 rounded-full transition-all ${
              wakeWordDetected || (isListening && !isAlwaysListening)
                ? 'bg-red-600 hover:bg-red-700 scale-110'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            data-testid="voice-mic-btn"
          >
            {isProcessing ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : wakeWordDetected ? (
              <MicOff className="w-6 h-6" />
            ) : (
              <Mic className="w-6 h-6" />
            )}
          </Button>
          
          {isSpeaking && (
            <Button
              onClick={stopAudio}
              variant="outline"
              className="h-10 px-3 border-slate-600 text-slate-400 hover:text-white"
              data-testid="voice-stop-btn"
            >
              <VolumeX className="w-4 h-4 mr-1" />
              Stop
            </Button>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 pb-2 text-center">
          <p className="text-[9px] text-slate-600">
            {LANGUAGES.find(l => l.code === language)?.name} | {VOICES.find(v => v.id === voice)?.name}
          </p>
        </div>
      </div>
    </div>
  );
}
